/**
 * Extra cards for /analytics/social: page profile visits, chat volume from
 * Facebook inboxes, best day to post, and the Instagram section.
 * Dumb components — the page computes/filters the data.
 */

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import { Link } from 'react-router-dom'
import {
  CalendarClock,
  ExternalLink,
  Eye,
  Heart,
  MessageCircle,
  MessagesSquare,
  Play,
  Radar,
  ShoppingCart,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SocialIg, SocialStatsResponse } from '@/hooks/useLiveVisitors'
import googleGLogo from '@/assets/google-g-2025.svg'
import { CouponUsagePanel } from './CouponUsagePanel'

const PLATFORM_LOGO: Record<string, string> = {
  Facebook: 'https://cdn.simpleicons.org/facebook/1877F2',
  Instagram: 'https://cdn.simpleicons.org/instagram/E4405F',
  Google: googleGLogo, // official 2025 gradient "G" (Wikimedia), inlined asset
  TikTok: 'https://cdn.simpleicons.org/tiktok/000000',
  LINE: 'https://cdn.simpleicons.org/line/06C755',
}

/** Ad campaigns detected purely from first-party traffic (click-IDs + UTM),
 * no ad-account integration. Shows what's running, paid vs organic, reach,
 * buyers touched, and a retargeting signal. */
export function DetectedCampaigns({
  data,
}: {
  data: NonNullable<SocialStatsResponse['campaigns']>
}) {
  const pc = data.paid_clicks
  const paidTotal = pc.meta + pc.google + pc.tiktok
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="dash-title text-foreground">แคมเปญที่ตรวจพบ</h3>
        <span className="dash-meta text-muted-foreground">
          จากทราฟฟิกของร้าน ไม่ต้องเชื่อม Ads · {data.window_days} วัน
        </span>
      </div>

      {/* Detected-signal chips */}
      <div className="mb-3 flex flex-wrap gap-2">
        {pc.meta > 0 && (
          <span className="flex items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-1 dash-meta">
            <img src={PLATFORM_LOGO.Facebook} alt="" className="h-3.5 w-3.5" />
            Meta ads · {NUM.format(pc.meta)} คลิก
          </span>
        )}
        {pc.google > 0 && (
          <span className="flex items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-1 dash-meta">
            <img src={PLATFORM_LOGO.Google} alt="" className="h-3.5 w-3.5" />
            Google ads · {NUM.format(pc.google)} คลิก
          </span>
        )}
        {pc.tiktok > 0 && (
          <span className="flex items-center gap-1.5 rounded-full border border-border/60 px-2.5 py-1 dash-meta">
            <img src={PLATFORM_LOGO.TikTok} alt="" className="h-3.5 w-3.5" />
            TikTok ads · {NUM.format(pc.tiktok)} คลิก
          </span>
        )}
        {data.retarget_people > 0 && (
          <span className="flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 dash-meta font-medium text-amber-600 dark:text-amber-400">
            <Radar className="h-3.5 w-3.5" />
            พบรีทาร์เก็ต · {NUM.format(data.retarget_people)} คนกลับมาจากโฆษณา
          </span>
        )}
      </div>

      {data.campaigns.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          ยังไม่พบแคมเปญที่มี UTM — {paidTotal > 0 ? 'มีคลิกโฆษณาแต่ไม่ได้ติดแท็ก UTM' : 'ยังไม่มีทราฟฟิกจากโฆษณา'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1140px] text-sm">
            <thead>
              <tr className="dash-meta uppercase tracking-wide text-muted-foreground/70 border-b border-border/40">
                <th className="py-1.5 text-left font-medium">แคมเปญ</th>
                <th className="py-1.5 pl-2 text-right font-medium">คน</th>
                <th className="py-1.5 pl-2 text-right font-medium">เทรนด์ 7 วัน</th>
                <th className="py-1.5 pl-2 text-right font-medium">Bounce</th>
                <th className="py-1.5 pl-2 text-right font-medium">หยิบใส่ตะกร้า</th>
                <th className="py-1.5 pl-2 text-right font-medium">ตะกร้าที่ทิ้ง</th>
                <th className="py-1.5 pl-2 text-right font-medium">CVR</th>
                <th className="py-1.5 pl-2 text-right font-medium">ผู้ซื้อ</th>
                <th className="py-1.5 pl-2 text-right font-medium">ลูกค้าใหม่</th>
                <th className="py-1.5 pl-2 text-right font-medium">สมาชิก</th>
                <th className="py-1.5 pl-2 text-right font-medium">AOV</th>
                <th className="py-1.5 pl-2 text-right font-medium">รายได้</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {data.campaigns.map((c) => (
                <tr key={c.name} className="hover:bg-muted/20">
                  <td className="py-2 pr-2">
                    <div className="flex items-center gap-2">
                      {PLATFORM_LOGO[c.platform] ? (
                        <img src={PLATFORM_LOGO[c.platform]} alt={c.platform} className="h-4 w-4 shrink-0" />
                      ) : (
                        <span className="h-4 w-4 shrink-0 rounded-full bg-muted" />
                      )}
                      <div className="min-w-0">
                        <p className="min-w-0 max-w-[180px] truncate text-xs text-foreground" title={c.name}>
                          {c.name}
                        </p>
                        {c.retargeting && (
                          <div className="mt-0.5 flex items-center gap-1">
                            <span className="rounded-full bg-amber-500/10 px-1.5 py-px dash-meta font-medium text-amber-600 dark:text-amber-400">
                              รีทาร์เก็ต
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 pl-2 text-right font-medium tabular-nums text-foreground">{NUM.format(c.people)}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">
                    {c.trend_pct == null ? (
                      <span className="text-muted-foreground/50">—</span>
                    ) : (
                      <span className={cn('font-medium', c.trend_pct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400')}>
                        {c.trend_pct >= 0 ? '▲' : '▼'} {Math.abs(c.trend_pct)}%
                      </span>
                    )}
                  </td>
                  <td className={cn('py-2 pl-2 text-right tabular-nums', c.bounce_pct >= 60 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground')}>
                    {c.sessions > 0 ? `${c.bounce_pct}%` : '—'}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums text-muted-foreground">
                    {c.carts_added > 0 ? (
                      <span title={`${NUM.format(c.carts_added)} คนหยิบใส่ตะกร้า`}>{c.atc_rate}%</span>
                    ) : '—'}
                  </td>
                  <td className={cn('py-2 pl-2 text-right tabular-nums', c.abandon_pct >= 70 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground')}>
                    {c.carts_added > 0 ? (
                      <span title={`${NUM.format(c.abandoned)} คนหยิบแล้วไม่ซื้อ`}>{c.abandon_pct}%</span>
                    ) : '—'}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums font-medium text-foreground">{c.cvr_pct > 0 ? `${c.cvr_pct}%` : '—'}</td>
                  <td className="py-2 pl-2 text-right tabular-nums text-muted-foreground">{c.buyers > 0 ? NUM.format(c.buyers) : '—'}</td>
                  <td className="py-2 pl-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">{c.new_customers > 0 ? NUM.format(c.new_customers) : '—'}</td>
                  <td className="py-2 pl-2 text-right tabular-nums text-sky-600 dark:text-sky-400">{c.new_members > 0 ? NUM.format(c.new_members) : '—'}</td>
                  <td className="py-2 pl-2 text-right tabular-nums text-muted-foreground">
                    {c.aov > 0 ? (
                      <span title={c.revenue_exact ? 'จากคำสั่งซื้อจริง' : 'ประมาณการจากทราฟฟิก (รอ UTM ครบ)'}>
                        {c.revenue_exact ? '' : '~'}฿{NUM.format(c.aov)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 pl-2 text-right tabular-nums font-medium text-foreground">
                    {c.revenue > 0 ? (
                      <span title={c.revenue_exact ? 'ยอดจริงจาก Frappe' : 'ประมาณการจากทราฟฟิก (รอ UTM ครบ)'}>
                        {c.revenue_exact ? '' : '~'}฿{NUM.format(c.revenue)}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 dash-meta text-muted-foreground/70">
        ทราฟฟิกจาก fbclid / gclid / utm · รายได้/CVR จากคำสั่งซื้อจริงใน Frappe — ยอดใช้จ่ายและ ROAS ต้องเชื่อมต่อบัญชีโฆษณา
      </p>
    </section>
  )
}


const NUM = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 })

export interface DayPoint {
  day: string
  value: number
}

function MiniCard({
  title,
  icon: Icon,
  sub,
  children,
}: {
  title: string
  icon: typeof Eye
  sub?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="dash-title text-foreground">{title}</h3>
        </div>
        {sub && <span className="dash-meta text-muted-foreground">{sub}</span>}
      </div>
      <div className="h-36">{children}</div>
    </section>
  )
}

/** Daily page profile visits (page_views_total). */
export function ProfileVisitsCard({ series, windowLabel = '28 วัน' }: { series: DayPoint[]; windowLabel?: string }) {
  const total = series.reduce((s, p) => s + p.value, 0)
  return (
    <MiniCard title="เข้าชมหน้าเพจ" icon={Eye} sub={`รวม ${NUM.format(total)} · ${windowLabel}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={34} tickFormatter={(v) => NUM.format(v)} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value) => [NUM.format(Number(value ?? 0)), 'เข้าชมเพจ']}
          />
          <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="hsl(var(--primary) / 0.12)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </MiniCard>
  )
}

/** Conversations opened per day on the pages' Facebook inboxes (Chatwoot). */
export function ChatVolumeCard({ series, windowLabel = '28 วัน' }: { series: DayPoint[]; windowLabel?: string }) {
  const total = series.reduce((s, p) => s + p.value, 0)
  return (
    <MiniCard title="แชทใหม่จาก Facebook" icon={MessagesSquare} sub={`รวม ${NUM.format(total)} · ${windowLabel}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={28} tickFormatter={(v) => NUM.format(v)} />
          <Tooltip
            cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value) => [NUM.format(Number(value ?? 0)), 'แชทใหม่']}
          />
          <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </MiniCard>
  )
}

/** Average engagement by day of week — "when should I post?". */
export function BestTimeCard({ series }: { series: DayPoint[] }) {
  const best = series.reduce((a, b) => (b.value > a.value ? b : a), series[0] ?? { day: '-', value: 0 })
  return (
    <MiniCard title="วันไหนโพสต์แล้วปัง" icon={CalendarClock} sub={`ดีสุด: วัน${best?.day ?? '-'}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={34} tickFormatter={(v) => NUM.format(v)} />
          <Tooltip
            cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value) => [NUM.format(Number(value ?? 0)), 'เอ็นเกจเมนต์เฉลี่ย/โพสต์']}
          />
          <Bar dataKey="value" fill="hsl(var(--primary) / 0.7)" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </MiniCard>
  )
}

/** Pixel/tracking health + retargetable audiences + recoverable carts —
 * the marketing "data pipeline" view. */
function AudienceSpark({ data, name }: { data: DayPoint[]; name: string }) {
  if (data.length < 2) return null
  return (
    <div className="mt-1.5 h-9">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <XAxis dataKey="day" hide />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, padding: '4px 8px' }}
            formatter={(value) => [NUM.format(Number(value ?? 0)), name]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="hsl(var(--primary))"
            strokeWidth={1.5}
            fill="hsl(var(--primary) / 0.1)"
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export function PipelineSection({
  pipeline,
  chatUsers,
  chatSeries,
}: {
  pipeline: NonNullable<SocialStatsResponse['pipeline']>
  chatUsers: number
  chatSeries: DayPoint[]
}) {
  const cfg = [
    { label: 'Meta Pixel', ok: pipeline.config.meta_pixel, logo: 'https://cdn.simpleicons.org/meta/0081FB' },
    { label: 'Conversions API', ok: pipeline.config.meta_capi, logo: 'https://cdn.simpleicons.org/meta/0081FB' },
    { label: 'GA4', ok: pipeline.config.ga4, logo: 'https://cdn.simpleicons.org/googleanalytics/E37400' },
    { label: 'Google Ads', ok: pipeline.config.google_ads, logo: 'https://cdn.simpleicons.org/googleads/4285F4' },
  ]
  const eventsByType = pipeline.audiences.events_by_type ?? []
  const drill: Record<string, { label: string; v: number; people: number }[]> = {
    $autocapture: pipeline.audiences.clicks_by_button ?? [],
    $pageview: pipeline.audiences.views_by_page ?? [],
  }
  const hourly = (pipeline.audiences.events_hourly ?? []).map((p2) => ({
    hour: p2.h.slice(11, 16),
    v: p2.v,
  }))
  const EVENT_TH: Record<string, string> = {
    $pageview: 'เปิดหน้าเว็บ',
    $autocapture: 'คลิก/กดปุ่ม',
    $pageleave: 'ออกจากหน้า',
    $rageclick: 'กดรัวๆ (หงุดหงิด)',
    order_placed: 'สั่งซื้อสำเร็จ',
    shared_order_placed: 'สั่งซื้อผ่านแชท',
    shared_checkout_mounted: 'เข้าหน้าชำระเงิน',
    payment_selected: 'เลือกวิธีชำระ',
    shipping_selected: 'เลือกการจัดส่ง',
    coupon_applied: 'ใช้คูปอง',
    quotation_requested: 'ขอใบเสนอราคา',
    $set: 'บันทึกโปรไฟล์ผู้ใช้',
    shared_payments_loaded: 'เปิดวิธีชำระ (แชท)',
    shared_addresses_loaded: 'เปิดที่อยู่จัดส่ง (แชท)',
    order_recovered: 'กู้คืนออเดอร์',
  }
  const seg = pipeline.segments
  const lineOa = pipeline.line_oa
  const toSpark = (rows: { d: string; v: number }[] | undefined) =>
    (rows ?? []).map((r) => ({ day: `${parseInt(r.d.slice(8, 10), 10)}/${parseInt(r.d.slice(5, 7), 10)}`, value: r.v }))
  const last7 = (rows: { d: string; v: number }[] | undefined) =>
    (rows ?? []).slice(-7).reduce((s2, r) => s2 + r.v, 0)
  const aud = [
    {
      label: 'คุกกี้ผู้เยี่ยมชม',
      value: pipeline.audiences.cookie_pool,
      sub: `Meta Custom Audience · +${NUM.format(last7(pipeline.audiences.cookie_daily))} ใน 7 วันล่าสุด`,
      spark: toSpark(pipeline.audiences.cookie_daily),
      sparkName: 'คนใหม่เข้าพูล/วัน',
    },
    {
      label: 'สมาชิก LINE',
      value: pipeline.audiences.line_members,
      sub: `ยิง LINE broadcast ได้ทันที · +${NUM.format(last7(pipeline.audiences.line_daily))} ใน 7 วัน`,
      spark: toSpark(pipeline.audiences.line_daily),
      sparkName: 'สมาชิกใหม่/วัน',
    },
    {
      label: 'คนทักแชท FB/IG',
      value: chatUsers,
      sub: `ตอบกลับ/ติดตามใน Inbox · +${NUM.format(chatSeries.slice(-7).reduce((s2, r) => s2 + r.value, 0))} แชทใน 7 วัน`,
      spark: chatSeries,
      sparkName: 'แชทใหม่/วัน',
    },
  ]
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Radar className="h-4 w-4 text-muted-foreground" />
          <h3 className="dash-title text-foreground">สุขภาพการติดตาม</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {cfg.map((c) => (
            <div
              key={c.label}
              title={`${c.label} — ${c.ok ? 'เชื่อมต่อแล้ว' : 'ยังไม่ตั้งค่า'}`}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 ${
                c.ok ? 'border-border/60' : 'border-dashed border-border/60 opacity-50'
              }`}
            >
              <span className="relative">
                <img src={c.logo} alt={c.label} className="h-4 w-4" loading="lazy" />
                <span
                  className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-card ${
                    c.ok ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                  }`}
                />
              </span>
              <span className="dash-meta font-medium text-foreground">{c.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-3">
          <div className="flex items-baseline justify-between">
            <p className="text-xs text-muted-foreground">เหตุการณ์ที่เก็บได้ · 24 ชม.</p>
            <p className="text-sm font-semibold tabular-nums text-foreground">
              {NUM.format(pipeline.audiences.events_24h)}
            </p>
          </div>
          <div className="mt-1 h-20">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={40}
                />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(value) => [NUM.format(Number(value ?? 0)), 'เหตุการณ์']}
                />
                <Bar dataKey="v" fill="hsl(var(--primary) / 0.75)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {eventsByType.length > 0 && (
            <div className="mt-2 divide-y divide-border/30">
              {eventsByType.slice(0, 6).map((e) => (
                <div key={e.event} className="py-1.5">
                  <div className="flex items-baseline gap-2 text-xs">
                    <span className="min-w-0 truncate text-foreground">
                      {EVENT_TH[e.event] ?? e.event}
                    </span>
                    <span className="shrink-0 dash-meta text-muted-foreground/70 font-mono">
                      {EVENT_TH[e.event] ? e.event : ''}
                    </span>
                    <span className="ml-auto shrink-0 tabular-nums font-medium text-foreground">
                      {NUM.format(e.v)}
                    </span>
                    <span className="w-16 shrink-0 text-right tabular-nums dash-meta text-muted-foreground">
                      {NUM.format(e.people)} คน
                    </span>
                  </div>
                  {(drill[e.event] ?? []).length > 0 && (
                    <div className="mt-1 space-y-0.5 pl-3 border-l border-border/40 ml-0.5">
                      {drill[e.event].slice(0, 4).map((d) => (
                        <div key={d.label} className="flex items-baseline gap-2 dash-meta">
                          <span className="min-w-0 truncate text-muted-foreground" title={d.label}>
                            {d.label}
                          </span>
                          <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                            {NUM.format(d.v)}
                          </span>
                          <span className="w-14 shrink-0 text-right tabular-nums dash-meta text-muted-foreground/70">
                            {NUM.format(d.people)} คน
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <h3 className="dash-title text-foreground">ออดิเอนซ์ที่รีทาร์เก็ตได้</h3>
        </div>
        {lineOa?.connected && (
          <div className="mb-4">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-foreground">เพื่อน LINE OA ทั้งหมด</span>
              <span className="text-lg font-semibold tabular-nums text-foreground">
                {NUM.format(lineOa.followers)}
              </span>
            </div>
            {(() => {
              const reachable = lineOa.targeted_reach
              const blocked = Math.max(0, lineOa.blocks)
              const other = Math.max(0, lineOa.followers - reachable - blocked)
              const pct = (n: number) =>
                lineOa.followers ? Math.round((n / lineOa.followers) * 100) : 0
              return (
                <>
                  <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-muted/60">
                    <div
                      className="h-full bg-emerald-500/80"
                      style={{ width: `${pct(reachable)}%` }}
                      title={`ส่งข้อความถึงได้ ${NUM.format(reachable)}`}
                    />
                    <div
                      className="h-full bg-red-400/70"
                      style={{ width: `${pct(blocked)}%` }}
                      title={`บล็อก ${NUM.format(blocked)}`}
                    />
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 dash-meta">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <span className="h-2 w-2 rounded-full bg-emerald-500/80" />
                      ส่งข้อความถึงได้ {NUM.format(reachable)} ({pct(reachable)}%)
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <span className="h-2 w-2 rounded-full bg-red-400/70" />
                      บล็อกเราไว้ {NUM.format(blocked)} ({pct(blocked)}%)
                    </span>
                    {other > 0 && (
                      <span className="text-muted-foreground/70">
                        อื่นๆ (บัญชีร้าง) {NUM.format(other)}
                      </span>
                    )}
                  </div>
                </>
              )
            })()}
          </div>
        )}
        <div className="space-y-3">
          {aud.map((a) => (
            <div key={a.label}>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-foreground">{a.label}</span>
                <span className="text-lg font-semibold tabular-nums text-foreground">
                  {NUM.format(a.value)}
                </span>
              </div>
              <p className="dash-meta text-muted-foreground/70">{a.sub}</p>
              <AudienceSpark data={a.spark} name={a.sparkName} />
            </div>
          ))}
        </div>
        {seg && (
          <div className="mt-4 border-t border-border/40 pt-3">
            <p className="mb-2 text-xs font-semibold text-foreground">
              เซกเมนต์พร้อมยิงแคมเปญ
              <span className="ml-1.5 font-normal dash-meta text-muted-foreground">28 วันล่าสุด</span>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'เริ่มเช็คเอาต์ ยังไม่ซื้อ', value: seg.hot_checkout, chip: 'ตามด่วน', hot: true },
                { label: 'ดูสินค้า ยังไม่ซื้อ', value: seg.viewers_no_buy, chip: 'Retarget Ads', hot: false },
                { label: 'เคยซื้อใน 90 วัน', value: seg.buyers_90d, chip: 'Lookalike / ขายซ้ำ', hot: false },
                { label: 'ลูกค้ามีเบอร์โทร', value: seg.customers_phone, chip: 'SMS', hot: false },
              ].map((r) => (
                <div key={r.label} className="rounded-lg border border-border/60 px-3 py-2.5">
                  <p className="text-lg font-semibold tabular-nums text-foreground leading-none">
                    {NUM.format(r.value)}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground leading-snug">{r.label}</p>
                  <span
                    className={
                      r.hot
                        ? 'mt-1.5 inline-block rounded-full bg-amber-500/10 px-2 py-0.5 dash-meta font-medium text-amber-600 dark:text-amber-400'
                        : 'mt-1.5 inline-block rounded-full bg-primary/10 px-2 py-0.5 dash-meta font-medium text-primary'
                    }
                  >
                    {r.chip}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
      </div>

      {/* Recovery (left) + live coupon usage (right) — side by side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <section className="rounded-xl border border-border bg-card p-4">
        <div className="mb-2 flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          <h3 className="dash-title text-foreground">ตะกร้าที่กู้คืนได้</h3>
          <span className="ml-auto dash-meta text-muted-foreground">
            {pipeline.carts.window_days} วันล่าสุด
          </span>
        </div>
        <p className="dash-metric text-foreground">
          ฿{NUM.format(pipeline.carts.value)}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {NUM.format(pipeline.carts.count)} ใบ · เฉลี่ย ฿
          {NUM.format(pipeline.carts.count ? pipeline.carts.value / pipeline.carts.count : 0)}/ใบ
        </p>

        {/* Contactable value split */}
        {(() => {
          const cv = pipeline.carts.contactable_value ?? 0
          const pct = pipeline.carts.value ? Math.round((cv / pipeline.carts.value) * 100) : 0
          return (
            <div className="mt-2.5">
              <div className="flex h-2 overflow-hidden rounded-full bg-muted/60">
                <div className="h-full bg-emerald-500/80" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 dash-meta text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-emerald-500/80" />
                  ติดตามได้ ฿{NUM.format(cv)} ({pct}%) · {NUM.format(pipeline.carts.contactable)} ใบ
                  มีเบอร์/LINE
                </span>
              </div>
            </div>
          )
        })()}

        {/* Fresh carts (24h) — recovery odds decay fast */}
        {(pipeline.carts.fresh_count ?? 0) > 0 && (
          <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2">
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              ยังร้อน · ทิ้งไว้ไม่ถึง 24 ชม.
            </span>
            <span className="ml-auto text-xs font-semibold tabular-nums text-amber-700 dark:text-amber-400">
              {NUM.format(pipeline.carts.fresh_count)} ใบ · ฿{NUM.format(pipeline.carts.fresh_value)}
            </span>
          </div>
        )}

        {/* Daily abandoned value */}
        {(pipeline.carts.daily ?? []).length > 1 && (
          <div className="mt-2.5 h-16">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={(pipeline.carts.daily ?? []).map((d) => ({
                  day: `${parseInt(d.d.slice(8, 10), 10)}/${parseInt(d.d.slice(5, 7), 10)}`,
                  value: d.value,
                  count: d.count,
                }))}
                margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis dataKey="day" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--primary) / 0.08)' }}
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  formatter={(value, _name, item) => [
                    `฿${NUM.format(Number(value ?? 0))} · ${NUM.format(item?.payload?.count ?? 0)} ใบ`,
                    'มูลค่าตะกร้าค้าง',
                  ]}
                />
                <Bar dataKey="value" fill="hsl(var(--primary) / 0.6)" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Most-stuck products */}
        {(pipeline.carts.top_items ?? []).length > 0 && (
          <div className="mt-2.5">
            <p className="mb-1 dash-meta font-medium text-muted-foreground">
              สินค้าค้างตะกร้ามากสุด
            </p>
            <div className="space-y-1">
              {(pipeline.carts.top_items ?? []).map((it) => (
                <div key={it.name} className="flex items-center gap-2 text-xs">
                  {it.image ? (
                    <img
                      src={it.image}
                      alt=""
                      loading="lazy"
                      className="h-6 w-6 shrink-0 rounded object-cover border border-border/40"
                    />
                  ) : (
                    <span className="h-6 w-6 shrink-0 rounded bg-muted" />
                  )}
                  <span className="min-w-0 truncate text-foreground">{it.name}</span>
                  <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                    {NUM.format(it.carts)} ใบ
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link
          to="/analytics/carts"
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
        >
          ดูรายการตะกร้า
          <ExternalLink className="h-3 w-3" />
        </Link>
      </section>

      <CouponUsagePanel />
      </div>
    </div>
  )
}

/** Instagram section — linked business account, media grid with engagement. */
export function InstagramSection({ ig }: { ig: SocialIg }) {
  return (
    <section className="rounded-xl border border-border bg-card px-4 sm:px-5 py-4">
      <div className="flex items-center gap-3 pb-3">
        {ig.picture ? (
          <img src={ig.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-border/40" />
        ) : (
          <span className="h-9 w-9 rounded-full bg-muted" />
        )}
        <div className="min-w-0 flex-1">
          <p className="dash-title text-foreground">
            <a
              href={`https://instagram.com/${ig.username}`}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              @{ig.username}
            </a>
          </p>
          <p className="text-xs text-muted-foreground">Instagram</p>
        </div>
        <div className="flex shrink-0 gap-5 text-right">
          <div>
            <p className="text-lg font-semibold tabular-nums text-foreground leading-none">
              {NUM.format(ig.followers)}
            </p>
            <p className="mt-0.5 dash-meta text-muted-foreground">ผู้ติดตาม</p>
          </div>
          <div>
            <p className="text-lg font-semibold tabular-nums text-foreground leading-none">
              {NUM.format(ig.media_count)}
            </p>
            <p className="mt-0.5 dash-meta text-muted-foreground">โพสต์ทั้งหมด</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {ig.media.map((m) => (
          <a
            key={m.id}
            href={m.permalink || undefined}
            target="_blank"
            rel="noreferrer"
            title={m.caption}
            className="group relative block aspect-square overflow-hidden rounded-lg border border-border/40 bg-muted"
          >
            {m.thumbnail && (
              <img
                src={m.thumbnail}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            )}
            {m.media_type === 'VIDEO' && (
              <Play className="absolute right-1.5 top-1.5 h-3.5 w-3.5 text-white drop-shadow" />
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4 dash-meta text-white opacity-0 transition-opacity group-hover:opacity-100">
              <span className="flex items-center gap-0.5">
                <Heart className="h-2.5 w-2.5" />
                {NUM.format(m.likes)}
              </span>
              <span className="flex items-center gap-0.5">
                <MessageCircle className="h-2.5 w-2.5" />
                {NUM.format(m.comments)}
              </span>
              <ExternalLink className="ml-auto h-2.5 w-2.5" />
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}
